import { List, ListItemAction, ListItemProperty } from "@/features/list";
import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Trash2Icon, ArchiveIcon, ArchiveRestoreIcon } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { NewFormulaTemplateDialog } from "@/features/kpi-admin/components/new-formula-template-dialog";
import {
  SlimTabs,
  SlimTabsList,
  SlimTabsTrigger,
} from "@/components/ui/slim-tabs";
import type { FormulaTemplate } from "@brewbase/db/types";
import { RequireCapability } from "@/components/require-capability";

export const Route = createFileRoute("/settings/formula-templates/")({
  component: () => (
    <RequireCapability cap="formula-template:edit">
      <RouteComponent />
    </RequireCapability>
  ),
});

function RouteComponent() {
  const utils = trpc.useUtils();
  const canEdit = useCan("formula-template:edit");
  const [tab, setTab] = useState<"active" | "archived">("active");

  const { data: templates, isLoading } = trpc.kpis.listTemplates.useQuery({
    includeArchived: tab === "archived",
  });

  const { data: archivedCount } =
    trpc.kpis.countArchivedTemplates.useQuery();

  const deleteTemplate = trpc.kpis.deleteTemplate.useMutation({
    onSuccess: () => {
      utils.kpis.listTemplates.invalidate();
      utils.kpis.countArchivedTemplates.invalidate();
      toast.success("Template deleted");
    },
    onError: (err) => toast.error(err.message),
  });

  const archiveMutation = trpc.kpis.archiveTemplate.useMutation({
    onSuccess: () => {
      utils.kpis.listTemplates.invalidate();
      utils.kpis.countArchivedTemplates.invalidate();
      toast.success("Template archived");
    },
    onError: (err) => toast.error(err.message),
  });

  const unarchiveMutation = trpc.kpis.unarchiveTemplate.useMutation({
    onSuccess: () => {
      utils.kpis.listTemplates.invalidate();
      utils.kpis.countArchivedTemplates.invalidate();
      toast.success("Template unarchived");
    },
    onError: (err) => toast.error(err.message),
  });

  const handleDelete = (id: FormulaTemplate["id"], name: string) => {
    if (
      window.confirm(
        `Delete template "${name}"? Existing instantiated formulas will not be affected.`,
      )
    ) {
      deleteTemplate.mutate(id);
    }
  };

  if (isLoading || !templates) {
    return (
      <div className="container mx-auto px-4 py-6">Loading templates...</div>
    );
  }

  // Filter by tab (server returns all when includeArchived=true)
  const filteredTemplates = templates.filter((t) => {
    const isArchived = !!t.archived_at;
    return tab === "archived" ? isArchived : !isArchived;
  });

  const listColumns: ListItemProperty<FormulaTemplate, keyof FormulaTemplate>[] = [
    {
      key: "name",
      label: "Name",
      fieldType: "string",
    },
    {
      key: "slug",
      label: "Slug",
      fieldType: "string",
      render: (value) => (
        <span className="font-mono text-xs">{String(value)}</span>
      ),
    },
    {
      key: "kind",
      label: "Kind",
      fieldType: "string",
    },
    {
      key: "unit",
      label: "Unit",
      fieldType: "string",
    },
    {
      key: "display_group",
      label: "Group",
      fieldType: "string",
      position: "right",
    },
  ];

  const actions: ListItemAction<FormulaTemplate>[] = !canEdit
    ? []
    : tab === "active"
      ? [
          {
            key: "archive",
            label: "Archive",
            position: "right",
            render: (item) => (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => archiveMutation.mutate(item.properties.id)}
                disabled={archiveMutation.isPending}
                title="Archive"
              >
                <ArchiveIcon className="size-4" />
              </Button>
            ),
          },
          {
            key: "delete",
            label: "Delete",
            position: "right",
            render: (item) => (
              <Button
                variant="destructiveGhost"
                size="icon"
                onClick={() =>
                  handleDelete(item.properties.id, item.properties.name)
                }
              >
                <Trash2Icon className="size-4" />
              </Button>
            ),
          },
        ]
      : [
          {
            key: "unarchive",
            label: "Unarchive",
            position: "right",
            render: (item) => (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => unarchiveMutation.mutate(item.properties.id)}
                disabled={unarchiveMutation.isPending}
                title="Unarchive"
              >
                <ArchiveRestoreIcon className="size-4" />
              </Button>
            ),
          },
          {
            key: "delete",
            label: "Delete",
            position: "right",
            render: (item) => (
              <Button
                variant="destructiveGhost"
                size="icon"
                onClick={() =>
                  handleDelete(item.properties.id, item.properties.name)
                }
              >
                <Trash2Icon className="size-4" />
              </Button>
            ),
          },
        ];

  return (
    <div className="container mx-auto px-4 py-6 flex flex-col grow min-h-0 gap-4">
      <div>
        <h1 className="text-lg font-semibold">Formula Templates</h1>
        <p className="text-sm text-muted-foreground">
          Reusable KPI formula library.
        </p>
      </div>
      <SlimTabs
        value={tab}
        onValueChange={(v) => setTab(v as "active" | "archived")}
      >
        <SlimTabsList>
          <SlimTabsTrigger value="active">
            Active ({tab === "active" ? filteredTemplates.length : "..."})
          </SlimTabsTrigger>
          <SlimTabsTrigger value="archived">
            Archived ({archivedCount ?? 0})
          </SlimTabsTrigger>
        </SlimTabsList>
      </SlimTabs>
      <List
        data={filteredTemplates.map((t) => ({
          id: t.id.toString(),
          href: `/settings/formula-templates/${t.id}`,
          properties: t,
        }))}
        properties={listColumns}
        actions={actions}
        customToolbar={
          canEdit && tab === "active" ? <NewFormulaTemplateDialog /> : undefined
        }
      />
    </div>
  );
}
